import { ExerciseStateMachine } from './ExerciseStateMachine';

export const RowState = {
  EXTENDED: 'EXTENDED',      
  PULLING: 'PULLING',        
  PULLED: 'PULLED',          
  RETURNING: 'RETURNING'     
};

// === FRONT-FACING ROWING DETECTION ===
// Key insight: For front-facing camera, focus on ELBOW ANGLE not elbow width
// Pulling = elbows bend (angle decreases), Extending = elbows straighten (angle increases)

const MIN_TIME_IN_POSITION = 200;  // Time to hold position before counting
const CONSECUTIVE_FRAMES_THRESHOLD = 3;  // Frames to confirm position
const MIN_TIME_BETWEEN_TRANSITIONS = 400; // Debounce rapid state changes

// Elbow angle thresholds (most reliable for front-facing)
// LENIENT for patients: don't require deep bend or full extension
const PULLED_ANGLE_THRESHOLD = 140;   // Elbow angle < 140° = pulled (even slight bend counts)
const EXTENDED_ANGLE_THRESHOLD = 155; // Elbow angle > 155° = extended (don't need perfectly straight)
const ANGLE_CHANGE_THRESHOLD = 2.0;   // Minimum angle change to count as movement (sensitive)

// Wrist-shoulder distance (secondary check)
const WRIST_SHOULDER_PULLED_RATIO = 0.6;   // Wrist distance < 60% of arm length = pulled
const WRIST_SHOULDER_EXTENDED_RATIO = 0.85; // Wrist distance > 85% of arm length = extended         

export class RowFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: RowState.EXTENDED,
      initialStateData: {
        previousElbowWidth: 0,
        previousTimestamp: 0,
        lastTransitionTime: 0,       // Debounce state transitions
        // Elbow angle tracking for ROM
        repStartElbowAngle: 0,       // Elbow angle at start of rep
        repMinElbowAngle: 180,       // Minimum angle during pull (most bent)

        rowAngle: 160,
        previousRowAngle: 160,
        elbowAngleHistory: [],
        angleChangeBuffer: [],
        isMovingBack: false,
        isMovingForward: false,

        consecutiveExtendedFrames: 0,
        consecutivePulledFrames: 0,

        lastExtendedTime: 0,
        lastPulledTime: 0,

        symmetryScore: 100,
        elbowBendScore: 100,
        controlScore: 100,
        postureStraightnessScore: 100,
        chestExpansionScore: 100,
        repFormScore: 0,

        exerciseProgress: 0
      },
      initialFormScores: {
        overall: 100,
        symmetryScore: 100,
        elbowBendScore: 100,
        controlScore: 100,
        postureStraightnessScore: 100,
        chestExpansionScore: 100
      },
      transitions: {
        [RowState.EXTENDED]: [
          {
            targetState: RowState.PULLING,
            condition: (metrics, stateData, timestamp) => {
              // Require significant movement AND time since last transition
              const timeSinceLastTransition = timestamp - (stateData.lastTransitionTime || 0);
              return metrics.isMovingBack && !metrics.isMovingForward &&
                     timestamp - stateData.lastPulledTime > 500 &&
                     timeSinceLastTransition > MIN_TIME_BETWEEN_TRANSITIONS;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastExtendedTime = timestamp;
              fsm.stateData.lastTransitionTime = timestamp;
              // Record starting elbow angle for ROM calculation
              fsm.stateData.repStartElbowAngle = metrics.elbowAngle;
              fsm.stateData.repMinElbowAngle = metrics.elbowAngle;
            }
          }
        ],
        
        [RowState.PULLING]: [
          {
            targetState: RowState.PULLED,
            condition: (metrics, stateData, timestamp) => {
              const timeSinceLastTransition = timestamp - (stateData.lastTransitionTime || 0);
              return metrics.isPulledPosition && !metrics.isMovingBack &&
                     timestamp - stateData.lastExtendedTime > MIN_TIME_IN_POSITION &&
                     timeSinceLastTransition > MIN_TIME_BETWEEN_TRANSITIONS;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastPulledTime = timestamp;
              fsm.stateData.lastTransitionTime = timestamp;

              // Calculate ROM based on elbow angle range (works for front-facing)
              const startAngle = fsm.stateData.repStartElbowAngle || EXTENDED_ANGLE_THRESHOLD;
              const currentAngle = metrics.elbowAngle || startAngle;
              const minAngle = Math.min(fsm.stateData.repMinElbowAngle || currentAngle, currentAngle);

              // ROM = how much elbow bent from start to minimum angle
              const angleRange = EXTENDED_ANGLE_THRESHOLD - PULLED_ANGLE_THRESHOLD; // Expected range
              const actualBend = startAngle - minAngle; // How much they actually bent
              const repROM = Math.min(100, Math.max(0, (actualBend / angleRange) * 100));

              // Store the ROM for this completed rep
              fsm.stateData.maxRangeOfMotion = Math.round(repROM);
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              // Pull depth = elbow bend + chest expansion
              const pullDepthScore = (metrics.elbowBendScore * 0.6) + (metrics.chestExpansionScore * 0.4);
              
              return Math.round(
                (metrics.symmetryScore * 0.35) +
                (pullDepthScore * 0.30) +
                (metrics.postureStraightnessScore * 0.20) +
                (metrics.controlScore * 0.15)
              );
            }
          },
          {
            targetState: RowState.EXTENDED,
            condition: (metrics, stateData, timestamp) => {
              const timeSinceLastTransition = timestamp - (stateData.lastTransitionTime || 0);
              return metrics.isExtendedPosition && metrics.isMovingForward && !metrics.isMovingBack &&
                     timeSinceLastTransition > MIN_TIME_BETWEEN_TRANSITIONS;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastTransitionTime = timestamp;
            }
          }
        ],

        [RowState.PULLED]: [
          {
            targetState: RowState.RETURNING,
            condition: (metrics, stateData, timestamp) => {
              const timeSinceLastTransition = timestamp - (stateData.lastTransitionTime || 0);
              return metrics.isMovingForward && !metrics.isMovingBack && !metrics.isPulledPosition &&
                     timeSinceLastTransition > MIN_TIME_BETWEEN_TRANSITIONS;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastTransitionTime = timestamp;
            }
          }
        ],

        [RowState.RETURNING]: [
          {
            targetState: RowState.EXTENDED,
            condition: (metrics, stateData, timestamp) => {
              const timeSinceLastTransition = timestamp - (stateData.lastTransitionTime || 0);
              return (metrics.isExtendedPosition || (metrics.elbowAngle > 150 && !metrics.isMovingBack)) &&
                     timeSinceLastTransition > MIN_TIME_BETWEEN_TRANSITIONS;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastExtendedTime = timestamp;
              fsm.stateData.lastTransitionTime = timestamp;
            }
          },
          {
            targetState: RowState.PULLING,
            condition: (metrics, stateData, timestamp) => {
              const timeSinceLastTransition = timestamp - (stateData.lastTransitionTime || 0);
              return metrics.isMovingBack && !metrics.isMovingForward && !metrics.isExtendedPosition &&
                     timeSinceLastTransition > MIN_TIME_BETWEEN_TRANSITIONS;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastTransitionTime = timestamp;
            }
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    if (!worldLandmarks[11] || !worldLandmarks[12] || 
        !worldLandmarks[13] || !worldLandmarks[14] || 
        !worldLandmarks[15] || !worldLandmarks[16]) {
      return {};
    }
    
    const leftShoulder = worldLandmarks[11];
    const rightShoulder = worldLandmarks[12];
    const leftElbow = worldLandmarks[13];
    const rightElbow = worldLandmarks[14];
    const leftWrist = worldLandmarks[15];
    const rightWrist = worldLandmarks[16];
    
    const shoulderWidth = this.calculateDistance3D(leftShoulder, rightShoulder);
    const elbowWidth = this.calculateDistance3D(leftElbow, rightElbow);
    
    const leftElbowAngle = this.calculateJointAngle(leftShoulder, leftElbow, leftWrist);
    const rightElbowAngle = this.calculateJointAngle(rightShoulder, rightElbow, rightWrist);
    const avgElbowAngle = (leftElbowAngle + rightElbowAngle) / 2;
    
    if (!this.stateData.elbowAngleHistory) {
      this.stateData.elbowAngleHistory = [];
    }
    
    if (!this.stateData.angleChangeBuffer) {
      this.stateData.angleChangeBuffer = [];
    }
    
    if (!this.stateData.wristZHistory) {
      this.stateData.wristZHistory = [];
    }
    
    const avgWristZ = (leftWrist.z + rightWrist.z) / 2;
    
    if (this.stateData.wristZHistory.length >= 5) {
      this.stateData.wristZHistory.shift();
    }
    this.stateData.wristZHistory.push(avgWristZ);
    
    let zMovement = 0;
    if (this.stateData.wristZHistory.length >= 2) {
      const zValues = [...this.stateData.wristZHistory];
      let totalZChange = 0;
      for (let i = 1; i < zValues.length; i++) {
        totalZChange += zValues[i] - zValues[i-1]; 
      }
      zMovement = totalZChange / (zValues.length - 1);
    }
    
    if (this.stateData.elbowAngleHistory.length >= 3) {
      this.stateData.elbowAngleHistory.shift();
    }
    this.stateData.elbowAngleHistory.push(avgElbowAngle);
    
    const smoothedElbowAngle = this.stateData.elbowAngleHistory.reduce((sum, angle) => sum + angle, 0) / 
                               Math.max(1, this.stateData.elbowAngleHistory.length);
    
    const previousAngle = this.stateData.rowAngle || smoothedElbowAngle;
    const angleChange = smoothedElbowAngle - previousAngle;
    
    if (this.stateData.angleChangeBuffer.length >= 5) {
      this.stateData.angleChangeBuffer.shift();
    }
    this.stateData.angleChangeBuffer.push(angleChange);
    
    const avgAngleChange = this.stateData.angleChangeBuffer.reduce((sum, change) => sum + change, 0) / 
                           Math.max(1, this.stateData.angleChangeBuffer.length);
    
    // === FRONT-FACING ROWING: Primary detection using ELBOW ANGLE ===
    // This is the most reliable metric when facing the camera

    // Calculate wrist-to-shoulder distance (how far wrists are from shoulders)
    const leftWristShoulderDist = this.calculateDistance3D(leftShoulder, leftWrist);
    const rightWristShoulderDist = this.calculateDistance3D(rightShoulder, rightWrist);
    const avgWristShoulderDist = (leftWristShoulderDist + rightWristShoulderDist) / 2;

    // Z-axis relative position (for secondary detection, less reliable front-facing)
    const leftWristToShoulderZ = leftWrist.z - leftShoulder.z;
    const rightWristToShoulderZ = rightWrist.z - rightShoulder.z;
    const avgWristToShoulderZ = (leftWristToShoulderZ + rightWristToShoulderZ) / 2;

    // Estimate full arm length (shoulder width is roughly proportional)
    const estimatedArmLength = shoulderWidth * 1.5; // Approximate
    const wristShoulderRatio = avgWristShoulderDist / estimatedArmLength;

    // Track min/max elbow angle for ROM calculation
    if (!this.stateData.minElbowAngle || smoothedElbowAngle < this.stateData.minElbowAngle) {
      this.stateData.minElbowAngle = smoothedElbowAngle;
    }
    if (!this.stateData.maxElbowAngle || smoothedElbowAngle > this.stateData.maxElbowAngle) {
      this.stateData.maxElbowAngle = smoothedElbowAngle;
    }

    // === MOVEMENT DETECTION (based on angle CHANGE, not absolute position) ===
    // Moving back (pulling) = elbow angle DECREASING (bending arms)
    // Moving forward (extending) = elbow angle INCREASING (straightening arms)
    const isMovingBack = avgAngleChange < -ANGLE_CHANGE_THRESHOLD;
    const isMovingForward = avgAngleChange > ANGLE_CHANGE_THRESHOLD;

    // === POSITION DETECTION (based on absolute elbow angle) ===
    // Extended = arms straight (high angle)
    // Pulled = arms bent (low angle)
    const isExtendedByAngle = smoothedElbowAngle > EXTENDED_ANGLE_THRESHOLD;
    const isPulledByAngle = smoothedElbowAngle < PULLED_ANGLE_THRESHOLD;

    // Secondary check: wrist-shoulder distance ratio
    const isExtendedByDistance = wristShoulderRatio > WRIST_SHOULDER_EXTENDED_RATIO;
    const isPulledByDistance = wristShoulderRatio < WRIST_SHOULDER_PULLED_RATIO;

    if (!this.stateData.consecutiveExtendedFrames) this.stateData.consecutiveExtendedFrames = 0;
    if (!this.stateData.consecutivePulledFrames) this.stateData.consecutivePulledFrames = 0;

    // Combined detection: angle is primary, distance is secondary confirmation
    const isExtended = isExtendedByAngle || (smoothedElbowAngle > 135 && isExtendedByDistance);
    const isPulled = isPulledByAngle || (smoothedElbowAngle < 130 && isPulledByDistance);

    if (isExtended) {
      this.stateData.consecutiveExtendedFrames++;
      this.stateData.consecutivePulledFrames = 0;
    } else if (isPulled) {
      this.stateData.consecutivePulledFrames++;
      this.stateData.consecutiveExtendedFrames = 0;
    } else {
      this.stateData.consecutiveExtendedFrames = Math.max(0, this.stateData.consecutiveExtendedFrames - 1);
      this.stateData.consecutivePulledFrames = Math.max(0, this.stateData.consecutivePulledFrames - 1);
    }

    const isExtendedPosition = this.stateData.consecutiveExtendedFrames >= CONSECUTIVE_FRAMES_THRESHOLD;
    const isPulledPosition = this.stateData.consecutivePulledFrames >= CONSECUTIVE_FRAMES_THRESHOLD;
    
    const elbowAngleDiff = Math.abs(leftElbowAngle - rightElbowAngle);
    const wristYDiff = Math.abs(leftWrist.y - rightWrist.y);
    const symmetryScore = Math.min(100, 100 - (elbowAngleDiff * 2) - (wristYDiff * 100));
    
    let elbowBendScore = 100;
    if (isPulledPosition) {
      const optimalPullAngle = 90;
      const angleDiff = Math.abs(smoothedElbowAngle - optimalPullAngle);
      elbowBendScore = Math.max(0, 100 - (angleDiff / 1.5));
    }
    
    let chestExpansionScore = 100;
    if (isPulledPosition) {
      const leftShoulderToElbowZ = leftElbow.z - leftShoulder.z;
      const rightShoulderToElbowZ = rightElbow.z - rightShoulder.z;
      const avgShoulderToElbowZ = (leftShoulderToElbowZ + rightShoulderToElbowZ) / 2;
      
      chestExpansionScore = Math.min(100, Math.max(0, 50 + avgShoulderToElbowZ * 500));
    }
    
    const shoulderElevation = Math.abs(leftShoulder.y - rightShoulder.y);
    const postureStraightnessScore = Math.max(0, 100 - (shoulderElevation * 500));
    
    let controlScore = 100;
    if (this.stateData.angleChangeBuffer.length >= 3) {
      let jitter = 0;
      for (let i = 1; i < this.stateData.angleChangeBuffer.length; i++) {
        const prevChange = this.stateData.angleChangeBuffer[i-1];
        const currChange = this.stateData.angleChangeBuffer[i];
        if (Math.sign(prevChange) !== Math.sign(currChange)) {
          jitter += Math.abs(currChange);
        }
      }
      controlScore = Math.max(0, 100 - (jitter * 5));
    }
    
    let exerciseProgress = 0;
    if (isPulledPosition) {
      exerciseProgress = 100;
    } else if (isExtendedPosition) {
      exerciseProgress = 0;
    } else if (isMovingBack) {
      exerciseProgress = Math.min(100, Math.max(0, 100 - ((smoothedElbowAngle - 90) / (160 - 90) * 100)));
    } else if (isMovingForward) {
      exerciseProgress = Math.max(0, Math.min(100, ((smoothedElbowAngle - 90) / (160 - 90) * 100)));
    }
    
    this.stateData.previousRowAngle = this.stateData.rowAngle;
    this.stateData.rowAngle = smoothedElbowAngle;
    this.stateData.previousElbowWidth = elbowWidth;
    this.stateData.previousTimestamp = timestamp;
    this.stateData.isMovingBack = isMovingBack;
    this.stateData.isMovingForward = isMovingForward;
    this.stateData.avgWristToShoulderZ = avgWristToShoulderZ;
    this.stateData.zMovement = zMovement;

    // Track minimum elbow angle during pull (most bent = lowest angle)
    if (this.currentState === RowState.PULLING || this.currentState === RowState.PULLED) {
      this.stateData.repMinElbowAngle = Math.min(
        this.stateData.repMinElbowAngle || 180,
        smoothedElbowAngle
      );
    }
    
    // Debug logging disabled for production - uncomment to debug
    // if (!this._debugFrameCount) this._debugFrameCount = 0;
    // this._debugFrameCount++;
    // if (this._debugFrameCount >= 20) {
    //   console.log('🚣 ROW:', this.currentState, `angle=${smoothedElbowAngle.toFixed(0)}°`);
    //   this._debugFrameCount = 0;
    // }
    
    // Calculate DISPLAY ROM based on elbow angle (more reliable for front-facing)
    // ROM = how much the elbow bends from straight (180°) to pulled position
    // Extended (180°) = 0% ROM, Fully pulled (90°) = 100% ROM
    let rangeOfMotion = 0;
    const angleRange = EXTENDED_ANGLE_THRESHOLD - PULLED_ANGLE_THRESHOLD; // e.g., 145 - 120 = 25°
    const currentBend = EXTENDED_ANGLE_THRESHOLD - smoothedElbowAngle; // How much bent from extended
    rangeOfMotion = Math.min(100, Math.max(0, (currentBend / angleRange) * 100));

    // Apply symmetry penalty (left/right arm difference)
    let symmetryFactor = 1.0;
    if (elbowAngleDiff > 30) {
      symmetryFactor = 0.7;
    } else if (elbowAngleDiff > 15) {
      symmetryFactor = 0.85;
    }
    rangeOfMotion = Math.round(rangeOfMotion * symmetryFactor);

    return {
      isMovingBack,
      isMovingForward,

      isExtendedPosition,
      isPulledPosition,

      elbowAngle: smoothedElbowAngle,
      leftElbowAngle,
      rightElbowAngle,
      angleChange: avgAngleChange,

      zMovement,
      avgWristToShoulderZ,

      symmetryScore,
      elbowBendScore,
      controlScore,
      postureStraightnessScore,
      chestExpansionScore,

      rangeOfMotion,  // Display ROM (real-time)
      elbowWidth,     // For ROM calculation
      shoulderWidth,  // For ROM calculation

      exerciseProgress
    };
  }
  
  updateFormScores(metrics, transitioned) {
    if (!metrics.elbowBendScore) return;

    this.formScores.symmetryScore = metrics.symmetryScore;
    this.formScores.elbowBendScore = metrics.elbowBendScore;
    this.formScores.controlScore = metrics.controlScore;
    this.formScores.postureStraightnessScore = metrics.postureStraightnessScore;
    this.formScores.chestExpansionScore = metrics.chestExpansionScore;
    this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion || 0);

    // Calculate pull depth score (combination of elbow bend + chest expansion)
    const pullDepthScore = Math.round(
      (metrics.elbowBendScore * 0.6) + 
      (metrics.chestExpansionScore * 0.4)
    );

    // Overall form score weighs: Symmetry (35%), Pull Depth (30%), Posture (20%), Control (15%)
    // Form score reflects QUALITY of rowing motion
    const currentFormScore = Math.round(
      (metrics.symmetryScore * 0.35) +
      (pullDepthScore * 0.30) +
      (metrics.postureStraightnessScore * 0.20) +
      (metrics.controlScore * 0.15)
    );

    // Update overall form score based on whether a rep was just completed
    if (this.stateData.repFormScore) {
      // If rep just completed, blend the rep score into overall
      this.formScores.overall = Math.round(this.formScores.overall * 0.7 + this.stateData.repFormScore * 0.3);
      this.stateData.repFormScore = 0;
    } else {
      // Otherwise, use current form score (real-time feedback)
      this.formScores.overall = currentFormScore;
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    if (!metrics.elbowBendScore) return null;
    
    // Priority feedback during active rowing
    if (currentState === RowState.PULLING || currentState === RowState.PULLED) {
      // Priority 1: Symmetry (most important for bilateral exercise)
      if (metrics.symmetryScore < 70) {
        return "Pull evenly with both arms";
      }
      
      // Priority 2: Pull depth (elbows back)
      if (metrics.elbowBendScore < 70) {
        return "Pull elbows further back";
      }
      
      // Priority 3: Chest expansion (shoulders back)
      if (metrics.chestExpansionScore < 70) {
        return "Squeeze shoulder blades together";
      }
      
      // Priority 4: Posture
      if (metrics.postureStraightnessScore < 65) {
        return "Keep shoulders level";
      }
    }
    
    // Control feedback (applies to all states)
    if (metrics.controlScore < 60) {
      return "Row with smoother motion";
    }
    
    // State-specific encouragement
    if (currentState === RowState.PULLED) {
      return "Good row! Return to start";
    } else if (currentState === RowState.EXTENDED) {
      return "Ready - pull elbows back";
    }
    
    return null;
  }
  
  calculateDistance3D(point1, point2) {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    const dz = point2.z - point1.z;
    return Math.sqrt(dx*dx + dy*dy + dz*dz);
  }
  
  calculateJointAngle(a, b, c) {
    const ba = {
      x: a.x - b.x,
      y: a.y - b.y,
      z: a.z - b.z
    };
    
    const bc = {
      x: c.x - b.x,
      y: c.y - b.y,
      z: c.z - b.z
    };
    
    const dotProduct = ba.x * bc.x + ba.y * bc.y + ba.z * bc.z;
    
    const baMagnitude = Math.sqrt(ba.x * ba.x + ba.y * ba.y + ba.z * ba.z);
    const bcMagnitude = Math.sqrt(bc.x * bc.x + bc.y * bc.y + bc.z * bc.z);
    
    if (baMagnitude === 0 || bcMagnitude === 0) {
      return 0;
    }
    
    let cosAngle = dotProduct / (baMagnitude * bcMagnitude);
    
    cosAngle = Math.max(-1, Math.min(1, cosAngle));
    
    const angleRadians = Math.acos(cosAngle);
    const angleDegrees = angleRadians * (180 / Math.PI);
    
    return angleDegrees;
  }
  
  process(poseData, timestamp) {
    return super.process(poseData, timestamp);
  }
} 